import React, { useState } from "react";
import { Mail, MapPin, Phone, Send, Loader2, CheckCircle2 } from "lucide-react";
import { submitContact } from "@/lib/api";
import { SERVICES, BRAND } from "@/lib/data";
import useSeo from "@/hooks/useSeo";

export default function ContactPage() {
  useSeo({
    title: "Contact Us",
    description:
      "Get in touch with ConnectHER. We respond to every inquiry within one business day.",
    path: "/contact",
  });
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  function update(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await submitContact(form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      setStatus("error");
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  }

  return (
    <>
      <section className="pt-16 md:pt-24 pb-10" data-testid="contact-hero">
        <div className="container-app max-w-4xl">
          <div className="eyebrow">Contact</div>
          <h1 className="mt-3 text-5xl md:text-6xl leading-tight">
            Let&apos;s connect.
          </h1>
          <p className="mt-5 text-lg text-navy/70 max-w-2xl leading-relaxed">
            Whether you have a question, an opportunity, or just want to say
            hello — we&apos;d love to hear from you. We respond to every
            inquiry within one business day.
          </p>
        </div>
      </section>

      <section className="pb-20" data-testid="contact-section">
        <div className="container-app grid md:grid-cols-12 gap-10">
          <aside className="md:col-span-5">
            <div className="card p-7 bg-navy text-soft border-navy">
              <h3 className="text-2xl text-soft">Reach us directly</h3>
              <div className="mt-6 space-y-5 text-sm">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-gold mt-0.5" />
                  <div>
                    <div className="text-soft/60 text-xs uppercase tracking-widest">Email</div>
                    <a href={`mailto:${BRAND.email}`} className="text-soft hover:text-gold transition">{BRAND.email}</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-gold mt-0.5" />
                  <div>
                    <div className="text-soft/60 text-xs uppercase tracking-widest">Phone</div>
                    <a href={`tel:${BRAND.phone}`} className="text-soft hover:text-gold transition">{BRAND.phone}</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gold mt-0.5" />
                  <div>
                    <div className="text-soft/60 text-xs uppercase tracking-widest">Location</div>
                    <div className="text-soft">{BRAND.location}</div>
                  </div>
                </div>
              </div>
              <div className="mt-7 pt-7 border-t border-soft/10 text-sm text-soft/70 leading-relaxed">
                We respond to all inquiries within 1 business day. For urgent
                client matters, please call the number above.
              </div>
            </div>
          </aside>

          <div className="md:col-span-7">
            {status === "success" ? (
              <div
                data-testid="contact-success"
                className="card p-10 flex flex-col items-start gap-3"
              >
                <CheckCircle2 className="w-10 h-10 text-teal" />
                <h3 className="text-3xl">Message received.</h3>
                <p className="text-navy/70 leading-relaxed">
                  Thank you for reaching out. A member of the ConnectHer team
                  will be in touch within one business day.
                </p>
                <button
                  onClick={() => setStatus("idle")}
                  className="btn-secondary mt-3"
                  data-testid="contact-send-another"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-testid="contact-form"
                className="card p-7 md:p-9 space-y-5"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-navy/60">Name *</label>
                    <input
                      type="text" required
                      value={form.name}
                      data-testid="contact-name"
                      onChange={(e) => update("name", e.target.value)}
                      className="input-field mt-2"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-navy/60">Email *</label>
                    <input
                      type="email" required
                      value={form.email}
                      data-testid="contact-email"
                      onChange={(e) => update("email", e.target.value)}
                      className="input-field mt-2"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-navy/60">Phone</label>
                    <input
                      type="tel"
                      value={form.phone}
                      data-testid="contact-phone"
                      onChange={(e) => update("phone", e.target.value)}
                      className="input-field mt-2"
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-navy/60">Service of Interest</label>
                    <select
                      value={form.service}
                      data-testid="contact-service"
                      onChange={(e) => update("service", e.target.value)}
                      className="input-field mt-2"
                    >
                      <option value="">Select a service</option>
                      {SERVICES.map((s) => (
                        <option key={s.slug} value={s.name}>{s.name}</option>
                      ))}
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-navy/60">How can we help? *</label>
                  <textarea
                    required
                    rows={5}
                    value={form.message}
                    data-testid="contact-message"
                    onChange={(e) => update("message", e.target.value)}
                    className="input-field mt-2 resize-none"
                    placeholder="Tell us a bit about your goals…"
                  />
                </div>
                {error && (
                  <p data-testid="contact-error" className="text-sm text-red-500">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="contact-submit"
                  className="btn-primary w-full sm:w-auto"
                >
                  {status === "loading" ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                  ) : (
                    <>Send Message <Send className="w-4 h-4" /></>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
